// HandLandmarker wrapper.
//
// Sub-agent (P1.2) contract:
//   const tracker = new HandsTracker({ camera });
//   await tracker.load();
//   tracker.start();
//   const f = tracker.lastFrame;   // { left, right }

import {
  FilesetResolver,
  HandLandmarker,
  type HandLandmarkerResult,
  type NormalizedLandmark,
  type Category,
} from '@mediapipe/tasks-vision';
import { emptyHand, type HandFrame, type HandsFrame, type Point } from '@pose-game/core';
import type { Camera } from './camera.js';

export interface HandsTrackerOptions {
  camera: Camera;
  modelAssetPath?: string;
  wasmBasePath?: string;
}

const DEFAULT_MODEL_PATH =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/latest/hand_landmarker.task';
const DEFAULT_WASM_BASE =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10/wasm';

const SWIPE_VELOCITY_PX_PER_SEC = 800;
// Pinch threshold expressed as a fraction of the per-hand "face_width_proxy".
// We approximate face_width_proxy with the palm scale (wrist->middle MCP)
// scaled by 1.5 (a face is ~1.5x palm length). Multiplied by 0.05 gives
// roughly 0.075 * palm length, which empirically yields a comfortable
// pinch detection threshold.
const PINCH_FRAC = 0.05;
const FACE_OVER_PALM = 1.5;

// MediaPipe Hands landmark indices used in our heuristics.
const WRIST = 0;
const THUMB_TIP = 4;
const INDEX_MCP = 5;
const INDEX_PIP = 6;
const INDEX_TIP = 8;
const MIDDLE_MCP = 9;
const MIDDLE_PIP = 10;
const MIDDLE_TIP = 12;
const RING_MCP = 13;
const RING_PIP = 14;
const RING_TIP = 16;
const PINKY_MCP = 17;
const PINKY_PIP = 18;
const PINKY_TIP = 20;
const THUMB_MCP = 1;

// Internal per-hand history used to derive velocity + edge-triggered events
// across frames.
interface HandHistory {
  wrist: Point | null;
  pinching: boolean;
  swiping: boolean;
  lastTimestamp: number;
}

const newHistory = (): HandHistory => ({
  wrist: null,
  pinching: false,
  swiping: false,
  lastTimestamp: 0,
});

const dist = (a: Point, b: Point): number => {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.hypot(dx, dy);
};

export class HandsTracker {
  private _frame: HandsFrame = { left: emptyHand(), right: emptyHand() };
  private landmarker: HandLandmarker | null = null;
  private unsubscribe: (() => void) | null = null;
  // Histories keyed by "user-side" (which is what we emit), so handedness
  // is already remapped before history lookup.
  private readonly leftHistory: HandHistory = newHistory();
  private readonly rightHistory: HandHistory = newHistory();
  private running = false;
  private lastInferenceTime = -1;

  constructor(private readonly opts: HandsTrackerOptions) {}

  async load(): Promise<void> {
    if (this.landmarker != null) return;
    const fileset = await FilesetResolver.forVisionTasks(
      this.opts.wasmBasePath ?? DEFAULT_WASM_BASE,
    );
    this.landmarker = await HandLandmarker.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: this.opts.modelAssetPath ?? DEFAULT_MODEL_PATH,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numHands: 2,
    });
  }

  start(): void {
    if (this.running) return;
    if (this.landmarker == null) {
      throw new Error('HandsTracker.start(): call load() first');
    }
    this.running = true;
    this.unsubscribe = this.opts.camera.onFrame(this.onCameraFrame);
  }

  stop(): void {
    this.running = false;
    if (this.unsubscribe != null) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    if (this.landmarker != null) {
      this.landmarker.close();
      this.landmarker = null;
    }
    this._frame = { left: emptyHand(), right: emptyHand() };
  }

  get lastFrame(): HandsFrame {
    return this._frame;
  }

  private readonly onCameraFrame = (): void => {
    if (!this.running) return;
    const lm = this.landmarker;
    if (lm == null) return;
    const camera = this.opts.camera;
    if (!camera.isReady) return;
    const video = camera.video;
    if (video.videoWidth === 0 || video.videoHeight === 0) return;

    // MediaPipe requires strictly monotonic timestamps in VIDEO mode.
    let ts = performance.now();
    if (ts <= this.lastInferenceTime) {
      ts = this.lastInferenceTime + 1;
    }
    this.lastInferenceTime = ts;

    let result: HandLandmarkerResult;
    try {
      result = lm.detectForVideo(video, ts);
    } catch {
      // Inference can fail transiently if the video frame isn't ready yet.
      return;
    }

    this._frame = this.buildFrame(result, camera.width, camera.height, ts);
  };

  private buildFrame(
    result: HandLandmarkerResult,
    width: number,
    height: number,
    timestamp: number,
  ): HandsFrame {
    // Map MediaPipe-handedness ("Left"/"Right" from camera POV) onto user-
    // side. Because the camera feed is rendered mirrored to the player,
    // MediaPipe's "Left" hand corresponds to the user's right hand and vice
    // versa.
    let userLeftIdx = -1;
    let userRightIdx = -1;
    const handednesses: Category[][] = result.handedness;
    for (let i = 0; i < handednesses.length; i++) {
      const cats = handednesses[i];
      const top: Category | undefined = cats?.[0];
      if (top == null) continue;
      const name = top.categoryName;
      if (name === 'Left') {
        userRightIdx = i;
      } else if (name === 'Right') {
        userLeftIdx = i;
      }
    }

    const left = this.buildHand(
      userLeftIdx >= 0 ? result.landmarks[userLeftIdx] : undefined,
      this.leftHistory,
      width,
      height,
      timestamp,
    );
    const right = this.buildHand(
      userRightIdx >= 0 ? result.landmarks[userRightIdx] : undefined,
      this.rightHistory,
      width,
      height,
      timestamp,
    );
    return { left, right };
  }

  private buildHand(
    normalized: NormalizedLandmark[] | undefined,
    history: HandHistory,
    width: number,
    height: number,
    timestamp: number,
  ): HandFrame {
    if (normalized == null || normalized.length < 21) {
      // Reset history so a re-acquired hand doesn't produce a spurious
      // velocity spike across the gap.
      history.wrist = null;
      history.pinching = false;
      history.swiping = false;
      history.lastTimestamp = timestamp;
      return emptyHand();
    }

    const landmarks: Point[] = new Array(normalized.length);
    for (let i = 0; i < normalized.length; i++) {
      const n = normalized[i];
      // noUncheckedIndexedAccess: n may be undefined per the type system.
      if (n == null) {
        landmarks[i] = { x: 0, y: 0 };
      } else {
        // Mirror x for selfie-feel rendering (matches face/pose).
        landmarks[i] = { x: (1 - n.x) * width, y: n.y * height };
      }
    }

    const wrist = landmarks[WRIST] ?? { x: 0, y: 0 };

    // Wrist velocity (px/sec).
    let velocity: Point = { x: 0, y: 0 };
    const prevWrist = history.wrist;
    const prevTs = history.lastTimestamp;
    if (prevWrist != null && prevTs > 0) {
      const dtMs = timestamp - prevTs;
      if (dtMs > 0) {
        const dtSec = dtMs / 1000;
        velocity = {
          x: (wrist.x - prevWrist.x) / dtSec,
          y: (wrist.y - prevWrist.y) / dtSec,
        };
      }
    }
    history.wrist = wrist;
    history.lastTimestamp = timestamp;

    // Swipe: rising-edge when |velocity| crosses the threshold.
    const speed = Math.hypot(velocity.x, velocity.y);
    const swipingNow = speed > SWIPE_VELOCITY_PX_PER_SEC;
    const swipeEvent = swipingNow && !history.swiping;
    history.swiping = swipingNow;

    // Pinch: rising-edge when thumb-tip and index-tip get closer than
    // PINCH_FRAC * face_width_proxy. We approximate face_width_proxy with
    // a multiple of palm scale (wrist -> middle MCP), which is robust to
    // distance from the camera.
    const thumb = landmarks[THUMB_TIP] ?? wrist;
    const indexTip = landmarks[INDEX_TIP] ?? wrist;
    const middleMcp = landmarks[MIDDLE_MCP] ?? wrist;
    const palmScale = Math.max(1, dist(wrist, middleMcp));
    const faceWidthProxy = palmScale * FACE_OVER_PALM;
    const pinchDist = dist(thumb, indexTip);
    const pinchingNow = pinchDist < PINCH_FRAC * faceWidthProxy;
    const pinchEvent = pinchingNow && !history.pinching;
    history.pinching = pinchingNow;

    // pointTarget: index tip — only when the index finger is extended.
    // Extended ≈ tip is farther from PIP than MCP is, i.e. the joint is
    // straightened out beyond the PIP.
    const indexPip = landmarks[INDEX_PIP] ?? wrist;
    const indexMcp = landmarks[INDEX_MCP] ?? wrist;
    const tipToPip = dist(indexTip, indexPip);
    const mcpToPip = dist(indexMcp, indexPip);
    const indexExtended = tipToPip > mcpToPip;
    const pointTarget: Point | null = indexExtended ? indexTip : null;

    const gesture = classifyGesture(landmarks);

    return {
      visible: true,
      landmarks,
      wristVelocity: velocity,
      swipeEvent,
      pinchEvent,
      pointTarget,
      gesture,
    };
  }
}

// ─── Gesture classification ────────────────────────────────────────────
//
// "Up" / "down" use image-y where smaller y = higher on screen.
// A finger is considered "up" when its tip y is above its PIP joint y
// (or, for the open-hand variant, above the MCP joint y).

const isFingerUp = (tip: Point | undefined, joint: Point | undefined): boolean => {
  if (tip == null || joint == null) return false;
  return tip.y < joint.y;
};

const isFingerDown = (tip: Point | undefined, joint: Point | undefined): boolean => {
  if (tip == null || joint == null) return false;
  return tip.y > joint.y;
};

function classifyGesture(
  landmarks: Point[],
): 'open' | 'fist' | 'point' | 'peace' | 'unknown' {
  const thumbTip = landmarks[THUMB_TIP];
  const thumbMcp = landmarks[THUMB_MCP];
  const indexTip = landmarks[INDEX_TIP];
  const indexMcp = landmarks[INDEX_MCP];
  const indexPip = landmarks[INDEX_PIP];
  const middleTip = landmarks[MIDDLE_TIP];
  const middleMcp = landmarks[MIDDLE_MCP];
  const middlePip = landmarks[MIDDLE_PIP];
  const ringTip = landmarks[RING_TIP];
  const ringMcp = landmarks[RING_MCP];
  const ringPip = landmarks[RING_PIP];
  const pinkyTip = landmarks[PINKY_TIP];
  const pinkyMcp = landmarks[PINKY_MCP];
  const pinkyPip = landmarks[PINKY_PIP];

  // Open: all 5 tips above MCP joints (tip y < mcp y).
  if (
    isFingerUp(thumbTip, thumbMcp) &&
    isFingerUp(indexTip, indexMcp) &&
    isFingerUp(middleTip, middleMcp) &&
    isFingerUp(ringTip, ringMcp) &&
    isFingerUp(pinkyTip, pinkyMcp)
  ) {
    return 'open';
  }

  // Fist: 4 non-thumb tips all below their PIP joints.
  if (
    isFingerDown(indexTip, indexPip) &&
    isFingerDown(middleTip, middlePip) &&
    isFingerDown(ringTip, ringPip) &&
    isFingerDown(pinkyTip, pinkyPip)
  ) {
    return 'fist';
  }

  // Per-finger up/down for 4 non-thumb fingers, compared against PIP.
  const indexUp = isFingerUp(indexTip, indexPip);
  const middleUp = isFingerUp(middleTip, middlePip);
  const ringUp = isFingerUp(ringTip, ringPip);
  const pinkyUp = isFingerUp(pinkyTip, pinkyPip);

  // Point: only index up.
  if (indexUp && !middleUp && !ringUp && !pinkyUp) {
    return 'point';
  }

  // Peace: index + middle up, ring + pinky down.
  if (indexUp && middleUp && !ringUp && !pinkyUp) {
    return 'peace';
  }

  return 'unknown';
}
